import { HttpStatusCode } from '@angular/common/http';
import { Injectable, effect, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../environments/environment';
import { CookieCategory } from '../interfaces/cookie-consent.interface';
import { CookieConsentService } from './cookie-consent.service';

/**
 * Sentry Error Tracking Service
 *
 * Manages Sentry initialization and tracking based on cookie consent.
 * Sentry.init() is ONLY called when user explicitly gives Analytics consent.
 *
 * This ensures GDPR compliance by not initializing tracking until consent is given.
 * Sentry will be initialized either:
 * 1. On page load if consent was previously given (loaded from localStorage)
 * 2. When user grants consent via the cookie consent modal
 *
 * Features:
 * - Respects GDPR cookie consent (opt-in model)
 * - Initializes Sentry only with explicit Analytics consent
 * - Automatically reacts to consent changes via signal-based reactivity
 * - Properly cleans up when consent is revoked
 *
 * @example
 * // Service automatically manages Sentry based on consent
 * // Just inject it in AppComponent to activate
 */
@Injectable({ providedIn: 'root' })
export class SentryService {
  readonly #cookieConsentService = inject(CookieConsentService);
  #isInitialized = false;

  constructor() {
    // React to preferences signal changes to handle both:
    // 1. Initial load from localStorage (async)
    // 2. Runtime changes when user updates consent
    effect(() => {
      const preferences = this.#cookieConsentService.preferences();
      const hasAnalyticsConsent = preferences[CookieCategory.ANALYTICS] === true;

      if (hasAnalyticsConsent) {
        this.initializeAndEnable();
      } else {
        this.disable();
      }
    });
  }

  /**
   * Initialize Sentry when user gives analytics consent
   * Only initializes once, subsequent calls are no-ops
   */
  private initializeAndEnable(): void {
    if (this.#isInitialized) {
      return;
    }

    if (!environment.production) {
      console.log('[Sentry] 🚀 Initializing Sentry with user consent');
    }

    Sentry.init({
      dsn: 'https://01753320009c03f2b6b0cb52bc8d641a@o4505352095989760.ingest.us.sentry.io/4507652238868480',
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      environment: environment.environment,
      enabled: true, // ✅ Enabled because user has given consent
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['*'],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      // Filter out errors we don't want to track
      beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
        const error = hint.originalException;

        // Exclude common HTTP client errors that don't require tracking
        if (error && typeof error === 'object' && 'status' in error) {
          const excludedStatusCodes = [
            HttpStatusCode.BadRequest, // User input validation errors
            HttpStatusCode.Unauthorized, // Handled by auth interceptor
            HttpStatusCode.Forbidden, // Expected permission errors
            HttpStatusCode.NotFound, // Common user navigation errors
            HttpStatusCode.TooManyRequests, // Rate limiting
          ];

          if (excludedStatusCodes.includes(error.status as number)) {
            return null;
          }
        }

        return event;
      },
    });

    this.#isInitialized = true;
  }

  /**
   * Disable Sentry when consent is not given or revoked
   * Note: Once initialized, Sentry cannot be fully uninitialized
   * We can only disable event capture
   */
  private disable(): void {
    if (!this.#isInitialized) {
      if (!environment.production) {
        console.log('[Sentry] Not initialized, no need to disable');
      }
      return;
    }

    const client = Sentry.getClient();
    if (!client) {
      return;
    }

    const options = client.getOptions();
    if (options.enabled) {
      options.enabled = false;
      if (!environment.production) {
        console.log('[Sentry] ❌ Disabled Sentry tracking (consent revoked)');
      }
    }
  }

  /**
   * Check if Sentry has been initialized
   */
  isInitialized(): boolean {
    return this.#isInitialized;
  }

  /**
   * Check if Sentry tracking is currently enabled
   */
  isEnabled(): boolean {
    if (!this.#isInitialized) {
      return false;
    }

    const client = Sentry.getClient();
    if (!client) {
      return false;
    }
    return client.getOptions().enabled ?? false;
  }
}
